import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { createAppTheme } from '../theme/createAppTheme'
import {
  ACCENT_PRESETS,
  ACCENT_STORAGE_KEY,
  DEFAULT_ACCENT_ID,
  resolveAccentMain,
} from '../theme/accentPresets'
import { applyMuahangCssVars } from '../theme/applyMuahangCssVars'
import { deriveMuahangFromMain } from '../theme/deriveAccentPalette'

export type ThemeAccentContextValue = {
  accentId: string
  setAccentId: (id: string) => void
  presets: typeof ACCENT_PRESETS
}

const ThemeAccentContext = createContext<ThemeAccentContextValue | null>(null)

function readStoredAccent(): string {
  try {
    const v = localStorage.getItem(ACCENT_STORAGE_KEY)
    if (v && ACCENT_PRESETS.some((p) => p.id === v)) return v
  } catch {
    /* ignore */
  }
  return DEFAULT_ACCENT_ID
}

export const ThemeAccentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accentId, setAccentIdState] = useState(readStoredAccent)

  const setAccentId = useCallback((id: string) => {
    const next = ACCENT_PRESETS.some((p) => p.id === id) ? id : DEFAULT_ACCENT_ID
    setAccentIdState(next)
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, next)
    } catch {
      /* ignore */
    }
  }, [])

  const theme = useMemo(() => createAppTheme(accentId), [accentId])

  useLayoutEffect(() => {
    applyMuahangCssVars(deriveMuahangFromMain(resolveAccentMain(accentId)))
  }, [accentId])

  const value = useMemo(
    () => ({ accentId, setAccentId, presets: ACCENT_PRESETS }),
    [accentId, setAccentId],
  )

  return (
    <ThemeAccentContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeAccentContext.Provider>
  )
}

export function useThemeAccent(): ThemeAccentContextValue {
  const ctx = useContext(ThemeAccentContext)
  if (!ctx) {
    throw new Error('useThemeAccent phải nằm trong ThemeAccentProvider')
  }
  return ctx
}
